/**
 * AnschreibenTab — Tab-Inhalt im Forderungsakte-Drawer (Issue #621)
 *
 * Drei Aktions-Buttons für den Mahnlauf:
 *   1. Anschreiben senden  — E-Mail-Draft, mahn_events (kanal=email), Stage +1
 *   2. Brief drucken       — PDF-Generierung, dokument, mahn_events (kanal=brief), Stage +1
 *   3. Telefonat erfassen  — Start/Stop-Timer, forderung_notizen + mahn_events (kanal=telefon)
 *
 * Stages 6–8 (Anwalt/Klage/Vollstreckung): kein Auto-Send, nur Vorlage-Preview.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Mail, Printer, Phone, PhoneOff, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { ForderungStagePill } from '@/components/forderung/ForderungStagePill';
import { getMahnvorlage, renderMahnvorlage, type MahnVariablen } from '@/lib/mahnvorlagen-render';
import { shouldAutoSend, nextStage } from '@/lib/anschreiben-actions';

// ─── Props ────────────────────────────────────────────────────────────────────

export interface AnschreibenTabProps {
  forderungsAkteId: string;
  stage: number;
  versicherungEmail: string | null;
  versicherungGegner: string | null;
  schadenNummer: string | null;
  rechnungsNr: string | null;
  betragOffen: number;
  onStageChange?: (newStage: number) => void;
}

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(n);
}

function computeFristBis(fristTage: number | null): string {
  if (!fristTage || fristTage <= 0) return '';
  const d = new Date();
  d.setDate(d.getDate() + fristTage);
  return d.toLocaleDateString('de-DE');
}

// ─── Komponente ───────────────────────────────────────────────────────────────

export function AnschreibenTab({
  forderungsAkteId,
  stage,
  versicherungEmail,
  versicherungGegner,
  schadenNummer,
  rechnungsNr,
  betragOffen,
  onStageChange,
}: AnschreibenTabProps) {
  // ── Vorlage-Preview ──────────────────────────────────────────────────────────
  const { session } = useAuth();
  const [vorschauOpen, setVorschauOpen] = useState(false);
  const [vorschauSubject, setVorschauSubject] = useState('');
  const [vorschauBodyHtml, setVorschauBodyHtml] = useState('');
  const [vorschauMode, setVorschauMode] = useState<'email' | 'brief'>('email');
  const [vorlageLoading, setVorlageLoading] = useState(false);
  const [vorlageError, setVorlageError] = useState<string | null>(null);

  // ── API-Action-Status ────────────────────────────────────────────────────────
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState(false);

  // ── Telefonat-Timer ──────────────────────────────────────────────────────────
  const [timerRunning, setTimerRunning] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [telefonatText, setTelefonatText] = useState('');
  const [telefonatSaving, setTelefonatSaving] = useState(false);
  const [telefonatError, setTelefonatError] = useState<string | null>(null);
  const [telefonatSuccess, setTelefonatSuccess] = useState(false);

  const templateStage = stage === 0 ? 1 : stage;
  const autoSend = shouldAutoSend(stage);
  const newStage = nextStage(stage);

  // ── Vorlage laden und Preview öffnen ─────────────────────────────────────────

  const openVorschau = useCallback(
    async (mode: 'email' | 'brief') => {
      setVorlageLoading(true);
      setVorlageError(null);
      setVorschauMode(mode);
      try {
        const vorlage = await getMahnvorlage(templateStage);

        const vars: MahnVariablen = {
          schuldner_name: versicherungGegner ?? '',
          rechnungs_nr: rechnungsNr ?? '',
          betrag_offen: betragOffen > 0 ? formatCurrency(betragOffen) : '',
          frist_bis: computeFristBis(vorlage.frist_tage),
          verwendungszweck: schadenNummer ?? rechnungsNr ?? '',
          tage_seit_faellig: '0',
          kunde_name: '',
          kennzeichen: '',
          schaden_nummer: schadenNummer ?? '',
        };

        setVorschauSubject(renderMahnvorlage(vorlage.betreff_template, vars));
        setVorschauBodyHtml(renderMahnvorlage(vorlage.body_html_template, vars));
        setVorschauOpen(true);
      } catch {
        setVorlageError('Vorlage konnte nicht geladen werden.');
      } finally {
        setVorlageLoading(false);
      }
    },
    [templateStage, versicherungGegner, rechnungsNr, betragOffen, schadenNummer],
  );

  // ── Anschreiben senden (bestätigt) ───────────────────────────────────────────

  const handleSendConfirm = useCallback(async () => {
    setSending(true);
    setSendError(null);
    try {
      const resp = await fetch('/api/forderung-send-anschreiben', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
        body: JSON.stringify({ forderungsakte_id: forderungsAkteId }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? 'Unbekannter Fehler');
      setVorschauOpen(false);
      setSendSuccess(true);
      onStageChange?.(json.new_stage);
    } catch (err: unknown) {
      setSendError(err instanceof Error ? err.message : 'Fehler beim Senden');
    } finally {
      setSending(false);
    }
  }, [forderungsAkteId, onStageChange]);

  // ── Brief drucken (bestätigt) ─────────────────────────────────────────────────

  const handleBriefConfirm = useCallback(async () => {
    setSending(true);
    setSendError(null);
    try {
      const resp = await fetch('/api/forderung-druck-brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
        body: JSON.stringify({ forderungsakte_id: forderungsAkteId }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? 'Unbekannter Fehler');
      setVorschauOpen(false);
      setSendSuccess(true);
      onStageChange?.(json.new_stage);
    } catch (err: unknown) {
      setSendError(err instanceof Error ? err.message : 'Fehler beim Drucken');
    } finally {
      setSending(false);
    }
  }, [forderungsAkteId, onStageChange]);

  // ── Telefonat-Timer ───────────────────────────────────────────────────────────

  const startTimer = useCallback(() => {
    setTimerRunning(true);
    timerRef.current = setInterval(() => setTimerSeconds((s) => s + 1), 1000);
  }, []);

  const stopTimer = useCallback(() => {
    setTimerRunning(false);
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleTelefonatSpeichern = useCallback(async () => {
    if (!telefonatText.trim()) return;
    setTelefonatSaving(true);
    setTelefonatError(null);
    try {
      const resp = await fetch('/api/forderung-erfass-telefonat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
        body: JSON.stringify({
          forderungsakte_id: forderungsAkteId,
          text: telefonatText.trim(),
          telefon_dauer_sekunden: timerSeconds,
        }),
      });
      const json = await resp.json();
      if (!resp.ok) throw new Error(json.error ?? 'Unbekannter Fehler');
      setTelefonatSuccess(true);
      setTelefonatText('');
      setTimerSeconds(0);
    } catch (err: unknown) {
      setTelefonatError(err instanceof Error ? err.message : 'Fehler beim Speichern');
    } finally {
      setTelefonatSaving(false);
    }
  }, [forderungsAkteId, telefonatText, timerSeconds]);

  // ── Render ────────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-5 p-4">
      {/* Stage-Anzeige */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Aktuelle Mahnstufe:</span>
          <ForderungStagePill stage={stage} forderungsAkteId={forderungsAkteId} />
        </div>
        {stage < 9 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Nächste Stufe:</span>
            <ForderungStagePill stage={newStage} />
          </div>
        )}
      </div>

      {/* Stages 6–8 Hinweis */}
      {stage >= 6 && stage <= 8 && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <span>
            Ab Stage 6 (juristische Eskalation) wird kein E-Mail-Draft automatisch angelegt.
            Die Vorlage wird nur zur Ansicht angezeigt.
          </span>
        </div>
      )}

      {/* Erfolgs-Banner */}
      {sendSuccess && (
        <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          <span>Aktion erfolgreich ausgeführt.</span>
        </div>
      )}

      {/* ── Button 1: Anschreiben senden ────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <div className="font-medium text-sm">Anschreiben senden</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {autoSend
                ? `Rendert Vorlage Stage ${templateStage} und legt E-Mail-Draft an (${versicherungEmail ?? 'kein Empfänger'})`
                : `Stage ${stage}: Nur Vorlage-Vorschau — kein automatischer E-Mail-Versand`}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={vorlageLoading}
              onClick={() => openVorschau('email')}
            >
              {vorlageLoading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Mail className="mr-2 h-4 w-4" />
              )}
              Vorschau
            </Button>
            {vorlageError && (
              <span className="text-xs text-red-600 self-center">{vorlageError}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ── Button 2: Brief drucken ───────────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <div className="font-medium text-sm">Brief drucken</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Generiert PDF, speichert in Dokumente, Stage +1
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            disabled={vorlageLoading}
            onClick={() => openVorschau('brief')}
          >
            <Printer className="mr-2 h-4 w-4" />
            Vorschau &amp; Drucken
          </Button>
        </CardContent>
      </Card>

      {/* ── Button 3: Telefonat erfassen ─────────────────────────────────────── */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div>
            <div className="font-medium text-sm">Telefonat erfassen</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Timer + Notiz → forderung_notizen + mahn_events (kanal=telefon)
            </div>
          </div>

          {/* Timer */}
          <div className="flex items-center gap-3">
            <span
              data-testid="telefonat-timer"
              className="font-mono text-lg tabular-nums w-14"
            >
              {formatSeconds(timerSeconds)}
            </span>
            {timerRunning ? (
              <Button variant="outline" size="sm" onClick={stopTimer}>
                <PhoneOff className="mr-2 h-4 w-4 text-red-600" />
                Stop
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={startTimer}
                disabled={telefonatSaving}
              >
                <Phone className="mr-2 h-4 w-4 text-green-600" />
                Start
              </Button>
            )}
          </div>

          {/* Notiz */}
          <Textarea
            placeholder="Gesprächsnotiz…"
            value={telefonatText}
            onChange={(e) => setTelefonatText(e.target.value)}
            className="min-h-[72px] resize-none text-sm"
            disabled={timerRunning || telefonatSaving}
          />

          {telefonatError && (
            <p className="text-xs text-red-600">{telefonatError}</p>
          )}

          {telefonatSuccess && (
            <p className="text-xs text-green-700 flex items-center gap-1">
              <CheckCircle2 className="h-3.5 w-3.5" />
              Telefonat gespeichert.
            </p>
          )}

          <Button
            size="sm"
            disabled={timerRunning || !telefonatText.trim() || telefonatSaving}
            onClick={handleTelefonatSpeichern}
          >
            {telefonatSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Telefonat speichern
          </Button>
        </CardContent>
      </Card>

      {/* ── Preview-Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={vorschauOpen} onOpenChange={setVorschauOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {vorschauMode === 'email' ? 'E-Mail-Vorschau' : 'Brief-Vorschau'} — Stage {templateStage}
            </DialogTitle>
            <DialogDescription className="font-medium text-foreground">
              {vorschauSubject}
            </DialogDescription>
          </DialogHeader>

          <div className="max-h-[40vh] overflow-y-auto rounded border bg-white p-4 text-sm">
            {vorschauBodyHtml ? (
              <div dangerouslySetInnerHTML={{ __html: vorschauBodyHtml }} />
            ) : (
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-4/6" />
              </div>
            )}
          </div>

          {!autoSend && vorschauMode === 'email' && (
            <Badge variant="outline" className="w-fit border-amber-300 bg-amber-50 text-amber-800">
              Nur Vorschau — kein E-Mail-Versand für Stage {stage}
            </Badge>
          )}

          {sendError && (
            <p className="text-sm text-red-600">{sendError}</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setVorschauOpen(false)}>
              Bearbeiten / Schließen
            </Button>
            {vorschauMode === 'email' && autoSend && (
              <Button onClick={handleSendConfirm} disabled={sending}>
                {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Senden &amp; Stage +1
              </Button>
            )}
            {vorschauMode === 'brief' && (
              <Button onClick={handleBriefConfirm} disabled={sending}>
                {sending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Brief drucken &amp; Stage +1
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
